import { useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"

import type { AlertSettingAccordion } from "types/alerts.types"

import {
  useOrgAlertSettingsQuery,
  useUpdateDeviceAlertSettingMutation,
  useUpdatePowerlineAlertSettingMutation,
  useUpdateTowerAlertSettingMutation,
} from "features/api"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"

interface UseSetAlertSettingActiveReturn {
  setActive: (active: boolean) => Promise<unknown>
  isLoading: boolean
}

// Turns a setting on or off with the update mutation of its entity.
const useSetAlertSettingActive = (
  alertSetting: AlertSettingAccordion,
): UseSetAlertSettingActiveReturn => {
  const { t } = useTranslation()
  const [updateDevice, deviceResult] = useUpdateDeviceAlertSettingMutation()
  const [updatePowerline, powerlineResult] = useUpdatePowerlineAlertSettingMutation()
  const [updateTower, towerResult] = useUpdateTowerAlertSettingMutation()
  const result =
    alertSetting.entity === "powerline"
      ? powerlineResult
      : alertSetting.entity === "tower"
        ? towerResult
        : deviceResult

  // The list brings the new state a moment after the request ends, so it stays in
  // flight until the setting shows the value it was set to.
  const requested = result.isSuccess ? result.originalArgs?.config.active : undefined
  const isWaiting =
    requested !== undefined && (alertSetting.config.active !== false) !== requested
  const { isSuccess, reset } = result
  // A failed refetch keeps the old value until the next poll, so it stops waiting.
  const { isError: isListError } = useOrgAlertSettingsQuery(
    { org_id: alertSetting.org_id },
    { selectFromResult: ({ isError }) => ({ isError }) },
  )
  useEffect(() => {
    if (isSuccess && (!isWaiting || isListError)) reset()
  }, [isSuccess, isWaiting, isListError, reset])

  const setActive = useCallback(
    (active: boolean) => {
      const values = {
        id: alertSetting.id,
        type: alertSetting.type,
        config: { ...alertSetting.config, active },
      }
      const request: Promise<unknown> | undefined = (() => {
        switch (alertSetting.entity) {
          case "device":
            return updateDevice({
              ...values,
              less_id: alertSetting.less_id,
              org_id: alertSetting.org_id,
            }).unwrap()
          case "powerline":
            return updatePowerline({
              ...values,
              powerlineId: alertSetting.entity_id,
              orgId: alertSetting.org_id,
            }).unwrap()
          case "tower":
            return updateTower({
              ...values,
              towerId: alertSetting.entity_id,
              orgId: alertSetting.org_id,
            }).unwrap()
          default:
            return undefined
        }
      })()
      if (!request) return Promise.resolve()

      return snackbarMutation({
        mutation: request,
        getErrorMessage: buildGetErrorMessage(t("alerts.ALERT_UPDATED_ERROR")),
        getSuccessMessage: () => t("alerts.ALERT_UPDATED_SUCCESS"),
      }).catch(() => {})
    },
    [alertSetting, t, updateDevice, updatePowerline, updateTower],
  )

  return {
    setActive,
    isLoading: result.isLoading || isWaiting,
  }
}

export default useSetAlertSettingActive
